import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { dayjs, fmtDT } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { PageHeader } from "@/components/Common";
import { PriorityBadge, StatusBadge } from "@/components/Badges";

const Section = ({ title, children, testid }) => (
  <div className="panel p-5" data-testid={testid}>
    <div className="eyebrow mb-3">{title}</div>
    {children}
  </div>
);

export default function Report() {
  const { hotelId } = useHotel();
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setR(null);
    api.get("/reports/monthly", { params: { month, hotel_id: hotelId } }).then((x) => setR(x.data)).catch((e) => toast.error(formatApiError(e)));
  }, [month, hotelId]);

  const exp = async (format) => {
    setBusy(format);
    try { await downloadFile("/reports/monthly/export", { month, hotel_id: hotelId, format }, `report-mse-${month}.${format}`); toast.success("Report scaricato"); }
    catch (e) { toast.error("Esportazione non riuscita"); }
    setBusy("");
  };

  return (
    <>
      <PageHeader eyebrow="Reportistica mensile" title="Report mensili" subtitle="KPI, ticket, incidenti, audit e azioni correttive del mese. Esportabile in PDF ed Excel per il Management Hotel.">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="report-month-input" />
        <Button variant="outline" className="gap-2" disabled={!!busy} onClick={() => exp("pdf")} data-testid="export-pdf-report-btn"><FileDown className="w-4 h-4" />{busy === "pdf" ? "…" : "PDF"}</Button>
        <Button className="gap-2" disabled={!!busy} onClick={() => exp("xlsx")} data-testid="export-excel-report-btn"><FileSpreadsheet className="w-4 h-4" />{busy === "xlsx" ? "…" : "Excel"}</Button>
      </PageHeader>
      {!r ? <div className="text-slate-500 font-mono text-sm">Caricamento…</div> : (
        <>
          <div className="text-sm text-slate-500" data-testid="report-hotels">{dayjs(`${month}-01`).format("MMMM YYYY")} · {r.hotels.join(" · ")}</div>
          <Section title="Indicatori di performance" testid="report-kpi-table">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              {r.kpi_rows.map(([label, value, target]) => (
                <div key={label} className="flex items-center justify-between py-2.5 border-b border-slate-200">
                  <span className="text-sm text-slate-700">{label}</span>
                  <span className="flex items-center gap-3"><span className="font-heading font-bold text-slate-900">{value}</span><span className="font-mono text-[11px] text-slate-500 w-20 text-right">{target}</span></span>
                </div>
              ))}
            </div>
          </Section>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title={`Ticket del mese (${r.tickets.length})`} testid="report-tickets">
              <div className="max-h-80 overflow-y-auto space-y-2">
                {r.tickets.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b border-slate-200">
                    <div className="min-w-0"><div className="truncate text-slate-800">{t.title}</div><div className="text-xs text-slate-500">{t.hotel_name} · {fmtDT(t.created_at)}</div></div>
                    <div className="flex gap-1.5 shrink-0"><PriorityBadge p={t.priority} /><StatusBadge value={t.status} /></div>
                  </div>
                ))}
              </div>
            </Section>
            <Section title={`Incidenti e near miss (${r.incidents.length})`} testid="report-incidents">
              <div className="max-h-80 overflow-y-auto space-y-2">
                {r.incidents.map((i) => (
                  <div key={i.id} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b border-slate-200">
                    <div><div className="text-slate-800">{i.category} · {i.location}</div><div className="text-xs text-slate-500">{i.hotel_name} · {fmtDT(i.created_at)}</div></div>
                    <StatusBadge value={i.type} />
                  </div>
                ))}
              </div>
            </Section>
          </div>
          <Section title={`Audit (${r.audits.length}) e non conformità (${r.nonconformities.length})`} testid="report-quality">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">{r.audits.map((a) => (
                <div key={a.id} className="flex justify-between text-sm border-b border-slate-200 py-1.5"><span className="text-slate-700">{a.area} · {a.hotel_name}</span><span className="font-bold text-amber-600">{a.score}</span></div>
              ))}</div>
              <div className="space-y-2">{r.nonconformities.map((n) => (
                <div key={n.id} className="flex justify-between gap-2 text-sm border-b border-slate-200 py-1.5"><span className="text-slate-700">{n.description}</span><StatusBadge value={n.status} /></div>
              ))}</div>
            </div>
          </Section>
        </>
      )}
    </>
  );
}
